import React, { useMemo } from "react";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { AnnoLightboxShell } from "./AnnoLighboxShell";
import { AnnoLightboxDisplay } from "./AnnoLightboxDisplay";
import { AnnoLightboxInput } from "./AnnoLightboxInput";
import { SM_AnnoLightbox } from "./SM_AnnoLightbox";
import {
  buildHierarchicalTagTypesConfig,
} from "./utils/buildHierarchicalTagTypesConfig";
import { createPortal } from "react-dom";
import { getTenant } from "@/TENANT_CONFIGS";

// Tabs rendered inside SM_AnnoLightbox under sub-themes for DUAL-AGENT tenants
// only — split into Thematics + Attributes, scoped client-side by
// meta.taxonomyRole on each subTheme option. Split-mode surfaces every
// sub-theme as its own flat section (no role tabs); legacy has no tabs either.
const ATTRIBUTE_THEMATIC_TABS = [
  { key: "thematic", display: "Thematics", taxonomyRole: "thematic" },
  { key: "attribute", display: "Attributes", taxonomyRole: "attribute" },
];

const AnnoLightbox = ({
  handleRemove,
  handleConfirmEdit,
  handleCancel,
  handleConfirmCreate,

  mode,
  setEditMode,

  dataId,
  data = {},
  nodeKeys,
  setData,
  position,
  handleClickOutside,
  annoConfig,
  readOnly,
  allowEdit, // when true, host (e.g. PublishedPage) has opted into annotation editing on a readOnly editor

  activeData_with_relatedNodeKeys
}) => {
  
  const { tagTypesConfig, toggleTagTypesConfig } = annoConfig || {};

  // Get platform configs to determine tenant type and hierarchy
  const platformConfigs = useGetQueryData('platformConfigs');
  const shouldUseSMLightbox = (
    platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable === true ||
    platformConfigs?.deployment?._AnnotationFramework?.framework === 'tags_subThemes_themes'
  );
  const annotationTagsCount = platformConfigs?.annotationTagsCount;
  const isDualAgent = getAnnoMode(platformConfigs) === "dual-agent";

  // Build hierarchical tagTypesConfig for SMT
  const hierarchicalTagTypesConfig = useMemo(() => {
    return buildHierarchicalTagTypesConfig({
      shouldUseSMLightbox,
      annotationTagsCount,
      ...(isDualAgent
        ? { attributeThematicTabs: ATTRIBUTE_THEMATIC_TABS }
        : {}),
    });
  }, [shouldUseSMLightbox, annotationTagsCount, tagTypesConfig, isDualAgent]);

  // NOTE: this component no longer reads the Lexical editor directly (no
  // useLexicalComposerContext) so it can be rendered standalone from a host
  // that has no editor in scope (e.g. an AnnoCard in PublishedPage's sidebar).
  // For the CREATE flow, the target text selection is captured by the host
  // (AnnoPlugin captures it when SHOW_ANNO_LIGHTBOX_COMMAND fires) and applied
  // inside handleConfirmCreate — see the comment there for why it's needed.

  /**
   * PENDING
   */
  // const endToEndSelectionData = editorState
  // ? isEndToEndSelection(editorState)
  // : false
  return (
    createPortal( 
    <AnnoLightboxShell {...{handleClickOutside, position}}>
      {mode === "display" ? (
        activeData_with_relatedNodeKeys.map(({textFragment, data : _data, dataId : _dataId, nodeKeys: _nodeKeys}) => (
          <AnnoLightboxDisplay
            //show textFrag only if its an overlapping annotations situation
            textFragment={activeData_with_relatedNodeKeys.length > 1 && textFragment}
            data={_data}
            dataId={_dataId}
            onRemove={() => handleRemove({ nodeKeys: _nodeKeys, dataId: _dataId })}
            onEdit={() => {
              setEditMode({data: _data, dataId: _dataId, nodeKeys: _nodeKeys});
            }}
            readOnly={readOnly}
            allowEdit={allowEdit}
            shouldUseSMLightbox={shouldUseSMLightbox}
            SELF_MANAGED_BASE_CONFIGS={platformConfigs?.SELF_MANAGED_BASE_CONFIGS}
          />
        ))
      ) : (
        // using this id to get the light box container element, to fix the lightbox hiding when there's no scroll up space
        <div id="input-light-box">
          {shouldUseSMLightbox ? (
            <SM_AnnoLightbox
              tagTypesConfig={hierarchicalTagTypesConfig}
              onChange={(tagType, value) => setData({ tagType, value })}
              value={data}
              handleOnConfirm={() => {
                mode === "edit" ? handleConfirmEdit({ data, dataId }) : handleConfirmCreate({ data });
              }}
              handleOnCancel={handleCancel}
            />
          ) : (
            <AnnoLightboxInput
              tagTypesConfig={tagTypesConfig}
              toggleTagTypesConfig={toggleTagTypesConfig}
              onChange={(tagType, value) => setData({ tagType, value })}
              value={data}
              handleOnConfirm={() => {
                mode === "edit" ? handleConfirmEdit({ data, dataId }) : handleConfirmCreate({ data });
              }}
              handleOnCancel={handleCancel}
            />
          )}
        </div>
      )}
    </AnnoLightboxShell> ,
    document.getElementById("annoLightbox-root"))
  );
};

export default AnnoLightbox;
